/**
 * 通用 API
 */
import http from '~/utils/http'
import { encryptData } from '~/utils/crypto'
import type { HttpResponse, RequestConfig } from '~/types/http'
import type {
  UniversalUploadParams,
  GetLyricsTimestampParams,
  GetMusicDetailParams
} from '~/types/app'

/**
 * 通用上传
 */
export async function universalUpload(
  params: UniversalUploadParams,
  file?: File | FormData,
  config?: Partial<RequestConfig>
): Promise<HttpResponse<string>> {
  if (file) {
    // 如果有文件，使用 POST 请求和 FormData
    let formData: FormData
    if (file instanceof FormData) {
      formData = file
    } else {
      formData = new FormData()
      formData.append('file', file)
    }

    // 将参数添加到 FormData
    Object.entries(params).forEach(([key, value]) => {
      formData.append(key, value)
    })

    return http.post('/upload/file', formData, {
      showSuccess: false,
      loading: false,
      ...config
    })
  } else {
    // 如果没有文件，使用 GET 请求
    return http.get('/upload/file', {
      params,
      showSuccess: false,
      loading: false,
      ...config
    })
  }
}

/**
 * 获取歌词的时间戳（请求数据加密）
 */
export async function getLyricsTimestamp(params: GetLyricsTimestampParams, config?: Partial<RequestConfig>): Promise<HttpResponse<string>> {
  return http.post('/queue/aligned_lyrics_v3_2_hw', {
    encryptedData: encryptData(params)
  }, {
    showSuccess: true,
    loading: true,
    ...config
  })
}

/**
 * 获取音乐详情（返回数据加密）
 */
export async function getMusicDetail(params: GetMusicDetailParams, config?: Partial<RequestConfig>): Promise<HttpResponse> {
  return http.get('/queue/getSongInfo', {
    params,
    showSuccess: false,
    loading: false,
    ...config
  })
}

/**
 * 增加音乐播放量
 */
export async function addPlayCount(params: { uuid: string }, config?: Partial<RequestConfig>): Promise<HttpResponse> {
  return http.get('/queue/add_play_count', {
    params,
    showSuccess: false,
    loading: false,
    ...config
  })
}

/**
 * 喜欢音乐
 */
export async function addUpvoteCount(params: { uuid: string }, config?: Partial<RequestConfig>) {
  return http.get('/queue/add_upvote_count', {
    params,
    showSuccess: false,
    loading: false,
    ...config
  })
}

/**
 * 取消喜欢音乐
 */
export async function cutUpvoteCount(params: { uuid: string }, config?: Partial<RequestConfig>) {
  return http.get('/queue/cut_upvote_count', {
    params,
    showSuccess: false,
    loading: false,
    ...config
  })
}

/**
 * 生成 WAV（请求数据加密）
 */
export async function convertWav(params: { clip_id: string }, config?: Partial<RequestConfig>): Promise<HttpResponse<string>> {
  return http.post('/queue/convert_wav_v2_hw', {
    encryptedData: encryptData(params)
  }, {
    showSuccess: true,
    loading: true,
    ...config
  })
}

/**
 * 生成 MIDI（请求数据加密）
 */
export async function convertMidi(params: { clip_id: string }, config?: Partial<RequestConfig>): Promise<HttpResponse<string>> {
  return http.post('/queue/midi_hw', {
    encryptedData: encryptData(params)
  }, {
    showSuccess: true,
    loading: true,
    ...config
  })
}

/**
 * 编辑音乐信息
 */
export async function editSongDetail(uuid: string, params: { cosUrl: string, title: string, prompt: string }, config?: Partial<RequestConfig>) {
  return http.post(`/queue/edit_song_detail?uuid=${uuid}`, params, {
    showSuccess: true,
    loading: true,
    ...config
  })
}

/**
 * 公开音乐
 */
export async function setIsPublic(params: { uuid: string }, config?: Partial<RequestConfig>) {
  return http.get('/queue/set_is_public', {
    params,
    showSuccess: false,
    loading: false,
    ...config
  })
}

/**
 * 取消公开音乐
 */
export async function setIsNotPublic(params: { uuid: string }, config?: Partial<RequestConfig>) {
  return http.get('/queue/set_is_not_public', {
    params,
    showSuccess: false,
    loading: false,
    ...config
  })
}

/**
 * 删除/恢复接口
 * @param params - 请求参数对象
 * @param params.isDelete - 操作类型：'1'表示删除，'0'表示恢复
 * @param params.uuids - 要操作的UUID，多个UUID用逗号分隔
 * @param config - 可选的请求配置，用于覆盖默认配置
 * @returns Promise响应对象
 */
export async function delClips(params: { isDelete: string, uuids: string }, config?: Partial<RequestConfig>) {
  return http.get('/queue/delClips', {
    params,
    showSuccess: false,
    loading: false,
    ...config
  })
}

/**
 * 设置音乐为非新音乐
 */
export async function setIsNotNew(params: { uuid: string }, config?: Partial<RequestConfig>) {
  return http.get('/queue/set_is_not_new', {
    params,
    showSuccess: false,
    loading: false,
    ...config
  })
}
